import { Layout, Menu, Modal, Alert, Checkbox, Popconfirm, Button } from "antd";
import {
  ArrowRightLeft,
  BadgeInfo,
  BedSingle,
  Bolt,
  Cable,
  CalendarCheck,
  CircleGauge,
  Cog,
  Component,
  DollarSign,
  Fan,
  FileSpreadsheet,
  Gem,
  Handshake,
  Headset,
  LandPlot,
  ListCheck,
  MapPin,
  MapPinIcon,
  Package,
  PackageIcon,
  PieChart,
  Plane,
  PlaneLanding,
  Scale,
  Shapes,
  Shield,
  Sparkle,
  SquareUser,
  Tag,
  Tally1,
  Tally2,
  Tally3,
  TrendingUp,
  Type,
  User2,
  UserPlus,
  Users,
  UsersRound,
  Wrench,
  BarChart,
  Settings,
  Trash2,
  Database,
} from "lucide-react";
import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useUserContext } from "../../context/users/users";
import toast from "react-hot-toast";
import axiosInstance from "../../axiosInstance";

const getLevelKeys = (items1) => {
  const key = {};
  const func = (items2, level = 1) => {
    items2.forEach((item) => {
      if (item.key) {
        key[item.key] = level;
      }
      if (item.children) {
        func(item.children, level + 1);
      }
    });
  };
  func(items1);
  return key;
};

const Sidebar = () => {
  const { pathname } = useLocation();
  const activeKey = pathname === "/" ? "/" : pathname.split("/")[1];
  const { loggedin_user_detail } = useUserContext();
  const [accountPermissions, setAccountPermissions] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [emptyModalVisible, setEmptyModalVisible] = useState(false);
  const [loadingEmpty, setLoadingEmpty] = useState(false);

  const handleEmptyDataSubmit = async () => {
    setLoadingEmpty(true);
    try {
      const response = await axiosInstance.post("Setting/emptyData");
      toast.success(response.data?.message || "All data emptied successfully!");
      setEmptyModalVisible(false);
      setTimeout(() => {
        window.location.reload();
      }, 800);
    } catch (error) {
      console.error("Error emptying data:", error);
      toast.error(error.response?.data?.message || "Failed to empty data.");
    } finally {
      setLoadingEmpty(false);
    }
  };

  useEffect(() => {
    try {
      // Get account permissions from localStorage with proper error handling
      const permissionsStr = localStorage.getItem("account_permissions");
      let permissions = [];

      if (permissionsStr) {
        try {
          permissions = JSON.parse(permissionsStr);
        } catch (parseError) {
          console.error("Error parsing account permissions:", parseError);
          toast.error("Error parsing account permissions");
          // If parsing fails, use an empty array
          permissions = [];
        }
      }

      setAccountPermissions(permissions);

      // Check if user is admin
      setIsAdmin(localStorage.getItem("is_admin") === "1");
    } catch (error) {
      console.error("Error setting up permissions:", error);
      toast.error("Error setting up permissions");
      setAccountPermissions([]);
      setIsAdmin(false);
    }
  }, [loggedin_user_detail]);

  const [stateOpenKeys, setStateOpenKeys] = useState(["2", "23"]);
  const onOpenChange = (openKeys) => {
    const currentOpenKey = openKeys.find(
      (key) => stateOpenKeys.indexOf(key) === -1,
    );
    // open
    if (currentOpenKey !== undefined) {
      const repeatIndex = openKeys
        .filter((key) => key !== currentOpenKey)
        .findIndex((key) => levelKeys[key] === levelKeys[currentOpenKey]);
      setStateOpenKeys(
        openKeys
          // remove repeat key
          .filter((_, index) => index !== repeatIndex)
          // remove current level all child
          .filter((key) => levelKeys[key] <= levelKeys[currentOpenKey]),
      );
    } else {
      // close
      setStateOpenKeys(openKeys);
    }
  };

  const hasPermission = (permission) => {
    return isAdmin || accountPermissions.includes(permission);
  };

  const items = [
    {
      key: "/",
      icon: (
        <div>
          <PieChart size={18} />
        </div>
      ),
      label: <Link to="/">Dashboard</Link>,
      // disabled: !hasPermission("dashboard"),
    },
    {
      key: "user",
      icon: (
        <div>
          <Users size={18} />
        </div>
      ),
      label: <Link to="/user">Users</Link>,
      disabled: !hasPermission("users"),
    },
    {
      key: "spacs",
      icon: (
        <div>
          <Package size={18} />
        </div>
      ),
      label: <Link to="/vehicle-specs">Vehicle Specs</Link>,
      disabled: !hasPermission("vehicle_specs"),
    }, {
      key: "valuation-summary",
      icon: (
        <div>
          <CircleGauge size={18} />
        </div>
      ),
      label: <Link to="/valuation-summary">Valuation Summary</Link>,
      disabled: !hasPermission("valuation_summary"),
    },
    {
      key: "average-mileage",
      icon: (
        <div>
          <CircleGauge size={18} />
        </div>
      ),
      label: <Link to="/average-mileage">Average Mileage</Link>,
      disabled: !hasPermission("average_mileage"),
    },
    {
      key: "time-depreciation",
      icon: (
        <div>
          <TrendingUp size={18} />
        </div>
      ),
      label: <Link to="/time-depreciation">Time Depreciation</Link>,
      disabled: !hasPermission("depreciation_band"),
    },
    {
      key: "sample",
      icon: (
        <div>
          <CircleGauge size={18} />
        </div>
      ),
      label: <Link to="/upload-sample">Sample File</Link>,
      disabled: !hasPermission("sample_file"),
    },
    {
      key: "audit",
      icon: (
        <div>
          <Cog size={18} />
        </div>
      ),
      label: <Link to="/audit">Audit</Link>,
      disabled: !hasPermission("audit"),
    },
    {
      key: "reports",
      icon: (
        <div>
          <FileSpreadsheet size={18} />
        </div>
      ),
      label: "Reports",
      children: [
        {
          key: "audit-report",
          label: <Link to="/audit-report">Audit Report</Link>,
          disabled: !hasPermission("audit_report"),
        },
        {
          key: "price-analysis",
          label: <Link to="/price-analysis">Price Analysis</Link>,
          disabled: !hasPermission("price_analysis"),
        },
      ],
    },
    {
      key: "rule",
      icon: (
        <div>
          <Shield size={18} />
        </div>
      ),
      label: <Link to="/rule">Rule</Link>,
      disabled: !hasPermission("rule"),
    },
    {
      key: "retail-price",
      icon: (
        <div>
          <DollarSign size={18} />
        </div>
      ),
      label: <Link to="/retail-price">Retail Price</Link>,
      disabled: !hasPermission("retail_price"),
    },
    {
      key: "make",
      icon: (
        <div>
          <Bolt size={18} />
        </div>
      ),
      label: <Link to="/make">Make</Link>,
      disabled: !hasPermission("make"),
    },
    {
      key: "data-backups",
      icon: (
        <div>
          <Database size={18} />
        </div>
      ),
      label: <Link to="/data-backups">Data Backups</Link>,
      disabled: !hasPermission("data_backup"),
    },
    {
      key: "settings",
      icon: (
        <div>
          <Settings size={18} />
        </div>
      ),
      label: <Link to="/settings">Settings</Link>,
      disabled: !hasPermission("settings"),
    },
    {
      key: "empty-data",
      icon: (
        <div>
          <Trash2 size={18} className="text-red-500" />
        </div>
      ),
      label: (
        <span
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setEmptyModalVisible(true);
          }}
          className="text-red-600 font-medium cursor-pointer"
        >
          Empty Data
        </span>
      ),
    },
  ];

  const levelKeys = getLevelKeys(items);

  function filterEnabledItems(items) {
    return items
      .filter((item) => !item.disabled)
      .map((item) => {
        if (item.children) {
          return {
            ...item,
            children: filterEnabledItems(item.children),
          };
        }
        return item;
      });
  }

  return (
    <Layout.Sider
      breakpoint="lg"
      collapsedWidth="0"
      theme="light"
      className="border-r-2"
      width={213}
    >
      <Link
        to="/"
        className="mx-4 mb-4 flex flex-col items-center justify-center border-b py-2 "
      >
        <div className="h-10 w-28 ">
          {/* <img
            src="/flysky/images/logo.png"
            alt="logo"
            className="h-full w-full cursor-pointer object-contain"
          /> */}
        </div>

        <h5 className="text-blue-1 text-xl font-bold italic  text-red-700">
          Auto Value
        </h5>
      </Link>
      <Menu
        mode="inline"
        selectedKeys={[activeKey]}
        openKeys={stateOpenKeys}
        onOpenChange={onOpenChange}
        onSelect={({ key }) => {
          if (key === "empty-data") {
            setEmptyModalVisible(true);
          }
        }}
        items={filterEnabledItems(items)}
        className="custom-menu border-none"
      />

      <Modal
        title={
          <div className="flex items-center gap-2 text-red-600">
            <Trash2 size={20} />
            <span className="font-bold text-lg">Empty System Data</span>
          </div>
        }
        open={emptyModalVisible}
        onCancel={() => setEmptyModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setEmptyModalVisible(false)}>
            Cancel
          </Button>,
          <Popconfirm
            key="confirm"
            title="Are you absolutely sure?"
            description="This will permanently delete all data! This action cannot be undone."
            onConfirm={handleEmptyDataSubmit}
            okText="Yes, Empty All Data"
            cancelText="No"
            okButtonProps={{ danger: true, loading: loadingEmpty }}
          >
            <Button danger type="primary" loading={loadingEmpty}>
              Empty All Data
            </Button>
          </Popconfirm>,
        ]}
        width={500}
        centered
      >
        <Alert
          message="Warning: Irreversible Action"
          description="Clicking 'Empty All Data' will permanently wipe all system data from the database. The following tables will be truncated:"
          type="warning"
          showIcon
          className="mb-4"
        />

        <ul className="list-disc pl-5 text-sm text-gray-700 space-y-1">
          <li><b>Vehicle Specs</b></li>
          <li><b>Time Depreciation</b></li>
          <li><b>Mileage Depreciation</b></li>
          <li><b>Monthly Depreciation</b></li>
          <li><b>Average Mileage</b></li>
          <li><b>Retail Price</b></li>
          <li><b>Make & Models</b></li>
        </ul>
      </Modal>
    </Layout.Sider>
  );
};

export default Sidebar;
